// @flow
import { useCallback, useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';

import type { User } from '@user/types';

import Toast from '_common/services/Toast/Toast';
import ModalArchivePeriods from '@groupClass/components/ModalArchivePeriods.jsx';
import ModalAccountingExport from '@groupClass/components/ModalAccountingExport';
import ModalClassExport from '@groupClass/components/ModalClassExport';
import ModalSmsExport from '@groupClass/components/ModalSmsExport';
import ModalKpiExport from '@groupClass/components/ModalKpiExport';
import ExportActionsMenu from '@banner/components/ExportActionsMenu';

import GroupClassesPeriodArchiveService, { type GroupClassesPeriodArchiveServiceData } from '@groupClass/services/GroupClassesPeriodArchiveService';
import AuthService, { type AuthServiceData } from '@user/services/AuthService';
import RolesHelpers from '@user/helpers/RolesHelpers';
import KpiExportService from '@groupClass/services/KpiExportService';

type Props = {
  children: React$Node,
};

const TitleBanner = (props: Props): React$Node => {
  const { children } = props;
  const { t } = useTranslation();

  const [ isActiveModalAccountingExport, setIsActiveModalAccountingExport ] = useState<boolean>(false);
  const [ isActiveModalClassExport, setIsActiveModalClassExport ] = useState<boolean>(false);
  const [ isActiveModalArchivePeriods, setIsActiveModalArchivePeriods ] = useState<boolean>(false);
  const [ isActiveModalSmsExport, setIsActiveModalSmsExport ] = useState<boolean>(false);
  const [ isArchivingAuthorized, setIsArchivingAuthorized ] = useState<boolean>(false);
  const [ isLoadingKpiExport, setIsLoadingKpiExport ] = useState<boolean>(false);
  const [ isActiveModalKpiExport, setIsActiveModalKpiExport ] = useState<boolean>(false);
  const [ exportStatus, setExportStatus ] = useState<string | null>(null);
  const [ exportDownloadUrl, setExportDownloadUrl ] = useState<string | null>(null);
  const [ currentUser, setCurrentUser ] = useState<User | null>(AuthService.user);

  const handleUpdateAuthService = (data: AuthServiceData) => {
    setCurrentUser(data.user);
  };

  const handleUpdatePeriodArchiveService = (data: GroupClassesPeriodArchiveServiceData): void => {
    if (data.groupClassesPeriodArchives.length > 0) {
      const lastArchivingYear = data.groupClassesPeriodArchives[0].archivedAt.getFullYear();
      const currentYear = new Date().getFullYear();
      if (currentYear === lastArchivingYear) {
        setIsArchivingAuthorized(false);
      } else {
        setIsArchivingAuthorized(true);
      }
    } else {
      setIsArchivingAuthorized(true);
    }
  };

  useEffect(() => AuthService.onChange(handleUpdateAuthService), []);
  useEffect(() => GroupClassesPeriodArchiveService.onChange(handleUpdatePeriodArchiveService), []);
  useEffect(() => {
    GroupClassesPeriodArchiveService.fetchAll();
  }, []);

  const toggleModalAccountingExport = useCallback((): void => {
    setIsActiveModalAccountingExport(!isActiveModalAccountingExport);
  }, [isActiveModalAccountingExport]);

  const toggleModalClassExport = useCallback((): void => {
    setIsActiveModalClassExport(!isActiveModalClassExport);
  }, [isActiveModalClassExport]);

  const toggleModalArchivePeriods = useCallback((): void => {
    setIsActiveModalArchivePeriods(!isActiveModalArchivePeriods);
  }, [isActiveModalArchivePeriods, setIsActiveModalArchivePeriods]);

  const toggleModalSmsExport = useCallback((): void => {
    setIsActiveModalSmsExport(!isActiveModalSmsExport);
  }, [isActiveModalSmsExport]);

  const handleKpiExport = useCallback(async (): Promise<void> => {
    setIsLoadingKpiExport(true);
    setExportStatus('queued');
    setExportDownloadUrl(null);
    setIsActiveModalKpiExport(true);
    try {
      const jobId = await KpiExportService.requestExport();
      setExportStatus('processing');
      const downloadUrl = await KpiExportService.pollUntilComplete(jobId);
      setExportDownloadUrl(downloadUrl);
      setExportStatus('completed');
    } catch {
      setExportStatus('failed');
    } finally {
      setIsLoadingKpiExport(false);
    }
  }, []);

  const handleCloseKpiExportModal = useCallback((): void => {
    setIsActiveModalKpiExport(false);
    setExportStatus(null);
    setExportDownloadUrl(null);
  }, []);

  const showSuccessMessage = useCallback((message: string): void => {
    Toast.success(t(message));
  }, [t]);

  const isAdmin = useMemo((): boolean => (
    RolesHelpers.isAdmin(currentUser)
  ), [currentUser]);

  const titleBannerToggleClassName = useMemo((): string => clsx({
    'title-banner-toggle': !isAdmin,
    'toggle-admin': !!isAdmin,
  }, [isAdmin]));

  return (
    <>
      <div className="title-banner">
        <div className="title-banner-title">
          <h1>
            { t('navigation.myGroupClasses') }
          </h1>
        </div>
        <div className={ titleBannerToggleClassName }>
          { children }
          { isAdmin && (
            <ExportActionsMenu
              onClassExport={ toggleModalClassExport }
              onAccountingExport={ toggleModalAccountingExport }
              onArchivePeriods={ toggleModalArchivePeriods }
              isArchivingAuthorized={ isArchivingAuthorized }
              onSmsExport={ toggleModalSmsExport }
              onKpiExport={ handleKpiExport }
              isKpiExportLoading={ isLoadingKpiExport }
            />
          ) }
        </div>
      </div>
      <ModalAccountingExport
        isActiveModalAccountingExport={ isActiveModalAccountingExport }
        toggleModalAccountingExport={ toggleModalAccountingExport }
      />
      <ModalClassExport
        isActiveModalExport={ isActiveModalClassExport }
        toggleModalExport={ toggleModalClassExport }
      />
      <ModalArchivePeriods
        toggleModal={ toggleModalArchivePeriods }
        isActive={ isActiveModalArchivePeriods }
        showSuccessMessage= { showSuccessMessage }
      />
      <ModalSmsExport
        isActive={ isActiveModalSmsExport }
        toggleModal={ toggleModalSmsExport }
      />
      <ModalKpiExport
        isActive={ isActiveModalKpiExport }
        status={ exportStatus }
        downloadUrl={ exportDownloadUrl }
        onClose={ handleCloseKpiExportModal }
        onRetry={ handleKpiExport }
      />
    </>
  );
};

export default TitleBanner;
